import { RefObject } from "react";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import {
  BOOKS_GLOBAL_STATUSES,
  getBooksGlobalStatusColor,
} from "constants/booksGlobalStatus";
import { useGetAllEntityBooksTaskQuery } from "store/apis/bookkeepingTracker";
import {
  BookkeepingTaskQueryParams,
  EntityTask,
} from "types/Models/bookkeepingTracker";

type StatusGroup = {
  label: string;
  count: number;
  color: TagColorType;
  priority: number;
};

const STATUS_CONFIG: Record<string, { color: TagColorType; priority: number }> =
  {
    "Connect Bank": { color: "red", priority: 1 },
    "Action required": { color: "red", priority: 1 },
    Completed: { color: "green", priority: 5 },
    "Not started": { color: "gray", priority: 4 },
  };

const getStatusConfig = (task: EntityTask, status: string) => {
  const booksStatus = task.books_global_status;
  // Books statuses follow their display order, Connect Bank first and Completed last
  if (booksStatus) {
    return {
      color: getBooksGlobalStatusColor(booksStatus.key),
      priority: BOOKS_GLOBAL_STATUSES.findIndex(
        ({ key }) => key === booksStatus.key
      ),
    };
  }
  return (
    STATUS_CONFIG[status] ?? { color: "yellow" as TagColorType, priority: 2 }
  );
};

const getStateName = (task: EntityTask): string => {
  if (task.books_global_status) return task.books_global_status.name;
  const { state } = task;
  if (!state) return "Not started";
  if (typeof state === "string") return state;
  return state.name || "Not started";
};

const MAX_VISIBLE_CHIPS = 3;

export const EntityTaskStatusSummary = ({
  entityId,
  filtersRef,
}: {
  entityId: string;
  filtersRef: RefObject<BookkeepingTaskQueryParams>;
}) => {
  const { data: tasks = [], isLoading } = useGetAllEntityBooksTaskQuery({
    entityId,
    ...filtersRef.current,
  });

  const statusGroups = tasks.reduce((acc, task) => {
    const status = getStateName(task);
    if (!acc[status]) {
      const { color, priority } = getStatusConfig(task, status);
      acc[status] = { label: status, count: 0, color, priority };
    }
    acc[status].count++;
    return acc;
  }, {} as Record<string, StatusGroup>);

  const sorted = Object.values(statusGroups).sort(
    (a, b) => a.priority - b.priority
  );
  const visible = sorted.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = sorted.length - MAX_VISIBLE_CHIPS;

  if (isLoading) {
    return (
      <div className="t-flex t-gap-1.5">
        <Skeleton className="t-h-5 t-w-20" />
        <Skeleton className="t-h-5 t-w-16" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return <span className="t-text-text-30 t-text-body-sm">—</span>;
  }

  return (
    <div className="t-flex t-flex-col t-gap-3 t-items-center">
      {visible.map(({ label, count, color }) => (
        <Tag key={label} icon={false} tagType={color} rounded size="small">
          {count} {label}
        </Tag>
      ))}
      {overflowCount > 0 && (
        <span className="t-text-body-sm t-text-text-30">
          +{overflowCount} more
        </span>
      )}
    </div>
  );
};
